import { Badge } from '@heidi/design-system/badge';
import { useTranslations } from '@heidi/runtime/i18n';
import { Globe, MapPinned } from 'lucide-react';

import type { Jurisdiction } from '../../contracts';

interface JurisdictionBadgeProps {
  jurisdiction: Jurisdiction;
}

/**
 * Tri-state jurisdiction chip for a citation source (EV-022 risk control).
 *
 *   - `inRegion`     → neutral "In region" chip (in-jurisdiction).
 *   - `global`       → neutral "Global" chip (global/world source).
 *   - `outOfRegion`  → neutral generic "Out of region" chip (not
 *                       `jurisdiction.label`) so clinicians see
 *                       out-of-jurisdiction guidance without implying a
 *                       specific country name in the chip text (EV-022).
 *
 * Vocabulary mirrors the backend `regional_relevance` wire field
 * (`service/chat/source_region_matching.py`); the BE wire value, the
 * camel-case UI state, the i18n key, and the chip text all read the same
 * way (`in_region` / `inRegion` / `chat.inRegion` / "In region").
 *
 * Guideline status is rendered separately by the caller; this badge only
 * conveys geographic relevance.
 */
export function JurisdictionBadge({ jurisdiction }: JurisdictionBadgeProps): JSX.Element {
  const { t } = useTranslations();

  if (jurisdiction.state === 'global') {
    return (
      <Badge color="neutral" size="sm" data-testid="jurisdiction-badge">
        <Globe />
        {t('chat.global')}
      </Badge>
    );
  }

  if (jurisdiction.state === 'outOfRegion') {
    return (
      <Badge color="neutral" size="sm" data-testid="jurisdiction-badge">
        <MapPinned />
        {t('chat.outOfRegion')}
      </Badge>
    );
  }

  return (
    <Badge color="neutral" size="sm" data-testid="jurisdiction-badge">
      <MapPinned />
      {t('chat.inRegion')}
    </Badge>
  );
}
